import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import usePageTitle from '../../hooks/usePageTitle';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import {
  ArrowTopRightOnSquareIcon,
  FolderOpenIcon,
  ListBulletIcon,
  PlayCircleIcon,
} from '@heroicons/react/24/outline';

import { useBreadcrumbs } from '../../contexts/BreadcrumbContext';
import { axiosInstance } from '../../routes/axiosInstance';
import {
  fetchActiveSessionCounts,
  openMarketSession,
  selectActiveSessionStatus,
  selectOpenMarketSessionLoading,
} from '../../slices/marketSlice';
import { formatBrusselsDateTime } from '../../components/SessionStatusChip';

const SESSION_STATUS_STYLES = {
  open: {
    label: 'Open',
    badgeClass:
      'border-emerald-200 bg-emerald-50 text-emerald-700 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-300',
  },
  closed: {
    label: 'Closed',
    badgeClass:
      'border-amber-200 bg-amber-50 text-amber-700 dark:border-amber-500/30 dark:bg-amber-500/10 dark:text-amber-300',
  },
  running: {
    label: 'Running',
    badgeClass:
      'border-sky-200 bg-sky-50 text-sky-700 dark:border-sky-500/30 dark:bg-sky-500/10 dark:text-sky-300',
  },
  finished: {
    label: 'Finished',
    badgeClass:
      'border-slate-200 bg-slate-100 text-slate-700 dark:border-slate-500/30 dark:bg-slate-500/10 dark:text-slate-200',
  },
  unknown: {
    label: 'Unknown',
    badgeClass:
      'border-gray-200 bg-gray-50 text-gray-700 dark:border-gray-600 dark:bg-gray-700/40 dark:text-gray-200',
  },
};

const NEXT_STATUS_MAP = {
  open: { status: 'closed', label: 'Closed' },
  closed: { status: 'running', label: 'Running' },
  running: { status: 'finished', label: 'Finished' },
};

const formatSessionValue = (value) => {
  const formatted = formatBrusselsDateTime(value);
  return formatted ? `${formatted} Brussels` : 'N/A';
};

const getStatusStyle = (status) => SESSION_STATUS_STYLES[status] || SESSION_STATUS_STYLES.unknown;

const buildStatusUpdatePayload = (session, nextStatus) => {
  const now = new Date().toISOString();
  const payload = { status: nextStatus };

  if (nextStatus === 'closed') {
    payload.close_ts = session.close_ts || now;
  }

  if (nextStatus === 'running') {
    payload.close_ts = session.close_ts || now;
    payload.launch_ts = session.launch_ts || now;
  }

  if (nextStatus === 'finished') {
    payload.close_ts = session.close_ts || now;
    payload.launch_ts = session.launch_ts || now;
    payload.finish_ts = session.finish_ts || now;
  }

  return payload;
};

const sortChallenges = (challenges) =>
  [...challenges].sort((a, b) => {
    const offsetDiff = (a.horizon_offset ?? 0) - (b.horizon_offset ?? 0);
    if (offsetDiff !== 0) {
      return offsetDiff;
    }

    return String(a.resource_name || '').localeCompare(String(b.resource_name || ''));
  });

function SessionSummarySection({
  title,
  session,
  loading,
  emptyText,
}) {
  const statusStyle = getStatusStyle(session?.status);
  const rows = session
    ? [
        { label: 'Session', value: `#${session.id}` },
        { label: 'Status', value: statusStyle.label },
        { label: 'Opened', value: formatSessionValue(session.open_ts) },
        { label: 'Gate closure', value: formatSessionValue(session.gate_closure) },
        ...(session.close_ts ? [{ label: 'Closed', value: formatSessionValue(session.close_ts) }] : []),
        ...(session.launch_ts ? [{ label: 'Launched', value: formatSessionValue(session.launch_ts) }] : []),
        ...(session.finish_ts ? [{ label: 'Finished', value: formatSessionValue(session.finish_ts) }] : []),
      ]
    : [];

  return (
    <div className="p-6">
      <div className="flex items-start justify-between gap-3">
        <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">{title}</h2>
        {session && (
          <span
            className={`inline-flex items-center rounded-full border px-3 py-1 text-xs font-semibold uppercase tracking-wide ${statusStyle.badgeClass}`}
          >
            {statusStyle.label}
          </span>
        )}
      </div>

      {loading ? (
        <div className="mt-4 text-sm text-gray-500 dark:text-gray-400">Loading session details...</div>
      ) : session ? (
        <dl className="mt-4 space-y-4 text-sm">
          {rows.map((row) => (
            <div key={row.label} className="flex items-start justify-between gap-4">
              <dt className="text-gray-500 dark:text-gray-400">{row.label}</dt>
              <dd className="text-right font-medium text-gray-900 dark:text-gray-100">{row.value}</dd>
            </div>
          ))}
        </dl>
      ) : (
        <div className="mt-4 rounded-lg border border-dashed border-gray-100 bg-gray-50/80 px-4 py-5 text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-700/30 dark:text-gray-400">
          {emptyText}
        </div>
      )}
    </div>
  );
}

export default function Operations() {
  usePageTitle('Operations');
  const dispatch = useDispatch();
  const activeSessionStatus = useSelector(selectActiveSessionStatus);
  const openMarketSessionLoading = useSelector(selectOpenMarketSessionLoading);
  const [loading, setLoading] = useState(false);
  const [currentSession, setCurrentSession] = useState(null);
  const [lastSession, setLastSession] = useState(null);
  const [currentChallenges, setCurrentChallenges] = useState([]);
  const [pendingStatus, setPendingStatus] = useState('');
  const [gateClosureHour, setGateClosureHour] = useState(10);

  useBreadcrumbs(
    [{ label: 'Operations' }, { label: 'Overview' }],
    'Manage live sessions and advance session status'
  );

  const loadSessions = useCallback(async () => {
    setLoading(true);
    try {
      const [openResp, closedResp, runningResp, lastResp] = await Promise.all([
        axiosInstance.get('/market/session/?status=open&latest_only=true'),
        axiosInstance.get('/market/session/?status=closed&latest_only=true'),
        axiosInstance.get('/market/session/?status=running&latest_only=true'),
        axiosInstance.get('/market/session/?status=finished&latest_only=true'),
      ]);

      const nextCurrentSession =
        (openResp.data?.data || [])[0] ||
        (closedResp.data?.data || [])[0] ||
        (runningResp.data?.data || [])[0] ||
        null;
      setCurrentSession(nextCurrentSession);
      setLastSession((lastResp.data?.data || [])[0] || null);

      if (nextCurrentSession?.id) {
        const challengesResp = await axiosInstance.get(
          `/market/challenge/?market_session=${nextCurrentSession.id}&open_only=true`
        );
        setCurrentChallenges(sortChallenges(challengesResp.data?.data || []));
      } else {
        setCurrentChallenges([]);
      }
    } catch (error) {
      toast.error('Failed to load operations overview');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadSessions();
  }, [loadSessions, activeSessionStatus.sessionId]);

  const handleOpenSession = async () => {
    try {
      await dispatch(openMarketSession({ gateClosureHour })).unwrap();
      toast.success('New market session opened');
      dispatch(fetchActiveSessionCounts());
      await loadSessions();
    } catch (error) {
      toast.error(typeof error === 'string' ? error : 'Failed to open market session');
    }
  };

  const handleStatusTransition = async () => {
    const nextTransition = currentSession ? NEXT_STATUS_MAP[currentSession.status] : null;
    if (!nextTransition) {
      return;
    }

    const nextStatus = nextTransition.status;
    setPendingStatus(nextStatus);

    try {
      await axiosInstance.patch(
        `/market/session/${currentSession.id}`,
        buildStatusUpdatePayload(currentSession, nextStatus)
      );

      toast.success(`Session #${currentSession.id} marked ${nextStatus}`);
      dispatch(fetchActiveSessionCounts());
      await loadSessions();
    } catch (error) {
      const message =
        error.response?.data?.detail ||
        error.response?.data?.message ||
        error.response?.data?.data ||
        'Failed to update market session';

      toast.error(typeof message === 'string' ? message : 'Failed to update market session');
    } finally {
      setPendingStatus('');
    }
  };

  return (
    <div className="grid grid-cols-1 gap-4 lg:grid-cols-3 lg:items-stretch">
      <div className="lg:col-span-1 flex h-full">
        <div className="flex h-full w-full flex-col divide-y divide-gray-100 rounded-xl border border-gray-200/70 bg-surface shadow-sm dark:divide-gray-700 dark:border-gray-600/70 dark:bg-gray-870">
          <SessionSummarySection
            title="Live Session"
            session={currentSession}
            loading={loading}
            emptyText="No session is currently open."
          />

          <SessionSummarySection
            title="Last Session"
            session={lastSession}
            loading={loading}
            emptyText="No finished sessions yet."
          />
        </div>
      </div>

      <div className="lg:col-span-2 flex h-full">
        <div className="flex h-full min-h-full w-full flex-col divide-y divide-gray-100 rounded-xl border border-gray-200/70 bg-surface shadow-sm dark:divide-gray-700 dark:border-gray-600/70 dark:bg-gray-870">
          <div className="p-6">
            <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Quick actions</h2>
            <p className="mt-2 text-sm text-gray-500 dark:text-gray-400">
              Start the next session when needed or update the active one from a single place.
            </p>
          </div>

          <div className="flex h-full flex-1 flex-col p-6">
            <div className="space-y-4">
              <div className="rounded-lg bg-gray-50/80 p-4 dark:bg-gray-870/30">
              {currentSession ? (
                <div>
                  <div className="flex items-start justify-between gap-3">
                    <div>
                      <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                        Session challenges
                      </p>
                      <p className="mt-2 text-base font-semibold text-gray-900 dark:text-gray-100">
                        {currentChallenges.length} {currentChallenges.length === 1 ? 'challenge' : 'challenges'}
                      </p>
                      <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                        Challenges in session #{currentSession.id}.
                      </p>
                    </div>
                    <ListBulletIcon className="h-5 w-5 text-slate-500 dark:text-slate-400" />
                  </div>

                  {currentChallenges.length > 0 ? (
                    <div className="mt-4 space-y-2">
                      {currentChallenges.map((challenge) => (
                        <div
                          key={challenge.id}
                          className="flex items-center justify-between gap-3 rounded-lg bg-surface px-3 py-2 text-sm dark:bg-gray-870"
                        >
                          <div className="min-w-0">
                            <p className="truncate font-medium text-gray-900 dark:text-gray-100">
                              {challenge.resource_name || 'Resource'}
                            </p>
                            <p className="truncate text-xs text-gray-500 dark:text-gray-400">
                              {challenge.challenge_type_label || challenge.use_case || 'Challenge'}
                              {challenge.target_day ? ` • ${challenge.target_day}` : ''}
                            </p>
                          </div>
                          <span className="shrink-0 rounded-full bg-slate-100 px-2.5 py-1 text-xs font-semibold text-slate-700 dark:bg-slate-700 dark:text-slate-200">
                            {challenge.horizon || `D+${challenge.horizon_offset ?? 0}`}
                          </span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <div className="mt-4 rounded-lg border border-dashed border-gray-100 bg-surface px-3 py-3 text-sm text-gray-500 dark:border-gray-700 dark:bg-gray-870 dark:text-gray-400">
                      No open challenges found for this session.
                    </div>
                  )}
                </div>
              ) : (
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                    Session challenges
                  </p>
                  <p className="mt-2 text-base font-semibold text-gray-900 dark:text-gray-100">
                    No live session
                  </p>
                  <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                    Open a new market session to generate challenges here.
                  </p>
                </div>
              )}
            </div>

            {!currentSession ? (
              <div className="rounded-lg bg-surface p-4 dark:bg-gray-870">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                  Session setup
                </p>
                <h3 className="mt-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Open a new session
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Choose the Brussels gate-closure hour for the next session before opening it.
                </p>

                <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-end">
                  <div className="min-w-0 flex-1">
                    <label htmlFor="session-gate-hour-select" className="block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Gate closure hour
                    </label>
                    <select
                      id="session-gate-hour-select"
                      value={gateClosureHour}
                      onChange={(event) => setGateClosureHour(Number(event.target.value))}
                      disabled={openMarketSessionLoading || Boolean(pendingStatus)}
                      className="mt-2 block w-full rounded-lg border border-gray-300 bg-surface px-3 py-3 text-sm text-gray-900 focus:border-brand-500 focus:outline-none focus:ring-2 focus:ring-brand-300 disabled:cursor-not-allowed disabled:opacity-60 dark:border-gray-600 dark:bg-gray-870 dark:text-gray-100 dark:focus:border-brand-400 dark:focus:ring-brand-500"
                    >
                      {Array.from({ length: 24 }, (_, hour) => (
                        <option key={hour} value={hour}>
                          {String(hour).padStart(2, '0')}:00 Brussels
                        </option>
                      ))}
                    </select>
                  </div>

                  <button
                    type="button"
                    onClick={handleOpenSession}
                    disabled={openMarketSessionLoading || Boolean(pendingStatus)}
                    className="inline-flex min-h-[56px] shrink-0 items-center justify-center gap-2 rounded-md border border-brand-500 bg-brand-50 dark:bg-brand-900/30 text-brand-700 dark:text-brand-300 hover:bg-brand-100 dark:hover:bg-brand-900/50 px-5 py-3 text-sm font-semibold transition disabled:cursor-not-allowed disabled:opacity-60 lg:min-w-[220px]"
                  >
                    <PlayCircleIcon className="h-5 w-5" />
                    {openMarketSessionLoading ? 'Opening session...' : 'Open New Session'}
                  </button>
                </div>
              </div>
            ) : (
              <div className="rounded-lg bg-surface p-4 dark:bg-gray-870">
                <p className="text-xs font-semibold uppercase tracking-[0.18em] text-slate-500 dark:text-slate-400">
                  Session control
                </p>
                <h3 className="mt-2 text-sm font-semibold text-gray-900 dark:text-gray-100">
                  Update session #{currentSession.id}
                </h3>
                <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
                  Choose the next state for the live session and apply the update from here.
                </p>

                <div className="mt-4 flex flex-col gap-3 lg:flex-row lg:items-end">
                  <div className="min-w-0 flex-1">
                    <p className="block text-xs font-medium uppercase tracking-wide text-gray-500 dark:text-gray-400">
                      Next status
                    </p>
                    <div className="mt-2 flex items-center rounded-lg border border-gray-300 bg-gray-50 px-3 py-3 text-sm font-medium text-gray-900 dark:border-gray-600 dark:bg-gray-870/50 dark:text-gray-100">
                      {NEXT_STATUS_MAP[currentSession.status]?.label ?? '—'}
                    </div>
                  </div>

                  <button
                    type="button"
                    onClick={handleStatusTransition}
                    disabled={Boolean(pendingStatus)}
                    className="inline-flex min-h-[56px] shrink-0 items-center justify-center gap-2 rounded-lg bg-slate-900 px-5 py-3 text-sm font-semibold text-white transition hover:bg-slate-800 disabled:cursor-not-allowed disabled:opacity-60 dark:bg-slate-100 dark:text-slate-900 dark:hover:bg-white lg:min-w-[220px]"
                  >
                    {pendingStatus ? 'Updating...' : 'Apply update'}
                  </button>
                </div>

              </div>
            )}

            <Link
              to="/data/resource/challenge-templates"
              className="inline-flex min-h-[56px] w-full items-center justify-between rounded-lg border border-gray-200 px-4 py-3 text-sm font-medium text-gray-900 transition hover:bg-gray-50 dark:border-gray-700 dark:text-gray-100 dark:hover:bg-gray-700/50"
            >
              <span className="inline-flex items-center gap-2">
                <FolderOpenIcon className="h-5 w-5 text-gray-500 dark:text-gray-400" />
                Manage Challenge Templates
              </span>
              <ArrowTopRightOnSquareIcon className="h-4 w-4 text-gray-400" />
            </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
